import React, { useState, useEffect } from "react";
import "./StockAdjustment.css";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
} from "@heroicons/react/20/solid";
import { Chip } from "@mui/material";
import Spinner from "../../../components/Spinner";
import AdjustStockCreationModal from "./components/AdjustStockCreationModal";
import { getAllStockAdjustments, getStockAdjustmentsSummary } from "../../../config/Services";
import AdjustmentDetailsModal from "./components/AdjustmentDetails";
import FilterModal from "./components/FilterModal";
import { useNavigate } from "react-router-dom";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { formatDate } from "../product/all-products/CustomerV2Preview/Component/helper/helper";
import { getStoreCurrencySymbol } from "../../../utils/currency";

const formatDateTime = (dateString) => {
  if (!dateString) return "NA";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "NA";
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month} ${day}, ${hours}:${minutes}`;
  } catch (e) {
    return "NA";
  }
};

const renderTypeBadge = (typeVal) => {
  if (!typeVal) return "NA";
  const typeUpper = typeVal.toUpperCase();
  
  if (typeUpper === "WASTAGE") {
    return (
      <span className="tag-common tag-wastage">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginRight: "4px" }}
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
        {typeUpper}
      </span>
    );
  } else if (typeUpper === "BROKEN" || typeUpper === "DAMAGED" || typeUpper === "REDUCE" || typeUpper === "SUBTRACT") {
    return (
      <span className="tag-common tag-broken">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginRight: "4px" }}
        >
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        {typeUpper}
      </span>
    );
  } else if (typeUpper === "CORRECTION ADD" || typeUpper === "ADD" || typeUpper === "CORRECTION_ADD") {
    return (
      <span className="tag-common tag-correction-add">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginRight: "4px" }}
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
        {typeUpper}
      </span>
    );
  }
  
  return <span className="tag-common" style={{ backgroundColor: "#f1f5f9", color: "#475569", borderColor: "#cbd5e1" }}>{typeUpper}</span>;
};

const StockAdjustment = () => {
  const [createStockOpen, setCreateStockOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [rowDetails, setRowDetails] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedAdjustmentId, setSelectedAdjustmentId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [selectedFilterLabel, setSelectedFilterLabel] = useState("Last 2 Month");
  const [reset, setReset] = useState(false);

  const licenseId = localStorage.getItem("licenseId");
  const sessionId = localStorage.getItem("sessionId");
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  let retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));
  const navigate = useNavigate();

  const [pageState, setPageState] = useState({
    loader: false,
    dataRows: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  // Initialize with default date range (Last 2 Months)
  useEffect(() => {
    const today = new Date();
    const endDate = today.toISOString().split("T")[0];
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const startDate = twoMonthsAgo.toISOString().split("T")[0];

    setDateRange({ startDate, endDate });
  }, []);

  const fetchSummaryData = async () => {
    const payload = {
      locationId: [locId],
      orgId: [orgId],
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      reason: null,
    };

    try {
      const response = await getStockAdjustmentsSummary(payload);

      if (response?.data?.data?.es === 0 && response?.data?.data?.statusCode === 200) {
        setSummaryData(response?.data?.data?.data);
      }
    } catch (error) {}
  };

  const fetchAdjustmentLogs = async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setPageState((old) => ({ ...old, loader: true }));

    const payload = {
      pageNo: pageState?.page - 1,
      pageSize: pageState?.pageSize,
      locationId: [locId],
      orgId: [orgId],
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      searchBox: searchText || null,
      reason: null,
    };

    try {
      const response = await getAllStockAdjustments(payload);

      const result = await response?.data?.data;

      if (result?.es === 0 && result?.statusCode === 200) {
        // Check if data is an array or a string message
        if (Array.isArray(result?.data?.data) && result?.data?.data?.length > 0) {
          // Map API response to custom table format (Original column fields preserved)
          const mappedData = result?.data?.data?.map((item) => ({
            id: item?.adjustmentId,
            productName: item?.itemName,
            adjustedQuantity: item?.adjustedQuantity || 0,
            adjustedValue: `${getStoreCurrencySymbol()}${item?.adjustedValue || 0}`,
            batch: item?.batch || "N/A",
            reason: item?.reason || "N/A",
            stockInHand: item?.stockInHand,
            batch: item?.batch,
            lastUpdated: item?.lastUpdated,
            adjustmentType: item?.adjustmentType,
            adjustedBy: item?.createdByName || item?.adjustedByName || item?.adjustedBy || "N/A",
            uom: item?.uom || item?.unit || item?.sellingUnits || "pcs",
          }));

          setPageState((old) => ({
            ...old,
            dataRows: mappedData,
            total: result?.data?.totalResult || mappedData?.length,
            loader: false,
          }));
        } else {
          // Handle "No adjustment logs found" case
          setPageState((old) => ({
            ...old,
            dataRows: [],
            total: 0,
            loader: false,
          }));
        }
      } else {
        setPageState((old) => ({
          ...old,
          dataRows: [],
          total: 0,
          loader: false,
        }));
      }
    } catch (error) {
      setPageState((old) => ({
        ...old,
        dataRows: [],
        total: 0,
        loader: false,
      }));
    }
  };

  // Fetch data on component mount and when page changes or date range changes
  useEffect(() => {
    if (orgId && locId && dateRange?.startDate && dateRange?.endDate) {
      fetchAdjustmentLogs();
    }
  }, [pageState?.page, pageState?.pageSize, dateRange, reset]);

  useEffect(() => {
    if (orgId && locId && dateRange?.startDate && dateRange?.endDate) {
      fetchSummaryData();
    }
  }, [dateRange, reset]);

  // Handle search with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (orgId && locId) {
        setPageState((old) => ({ ...old, page: 1 })); // Reset to first page on search
        fetchAdjustmentLogs();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  const handleClose = () => {
    setCreateStockOpen(false);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedAdjustmentId(null);
  };

  const handleFilterOpen = () => {
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  const handleApplyFilter = (newDateRange, filterLabel) => {
    setDateRange(newDateRange);
    setSelectedFilterLabel(filterLabel);
    setPageState((old) => ({ ...old, page: 1 })); // Reset to first page when filter changes
  };

  const handleClearFilter = () => {
    // Reset to default Last 2 Months
    const today = new Date();
    const endDate = today.toISOString().split("T")[0];
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const startDate = twoMonthsAgo.toISOString().split("T")[0];

    setDateRange({ startDate, endDate });
    setSelectedFilterLabel("Last 2 Month");
    setPageState((old) => ({ ...old, page: 1 }));
  };

  const getFilterLabelDisplay = (label) => {
    const labelMap = {
      today: "Today",
      thisWeek: "This Week",
      thisMonth: "This Month",
      last2Month: "Last 2 Months",
      custom: "Custom Range",
    };
    return labelMap[label] || label;
  };

  const handleRowClick = (row) => {
    setSelectedAdjustmentId(row);
    setDetailsOpen(true);
  };

  return (
    <Navbar>
      <div className="stock-adj-container">
        {/* Sticky Header */}
        <div className="stock-adj-header">
          <div className="stock-adj-header-content">
            <div className="stock-adj-header-left">
              <button
                className="stock-adj-back-btn"
                onClick={() => navigate(-1)}
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-left w-5 h-5 text-slate-600"
                  aria-hidden="true"
                >
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
              </button>
              <h1 className="stock-adj-title">Stock Adjustment</h1>
            </div>
            <button
              className="stock-adj-btn-primary"
              type="button"
              onClick={() => setCreateStockOpen(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-plus w-4 h-4"
                aria-hidden="true"
              >
                <path d="M5 12h14"></path>
                <path d="M12 5v14"></path>
              </svg>
              New Adjustment
            </button>
          </div>
        </div>

        {/* Page Content Body */}
        <div className="stock-adj-body">
          {/* Controls: Search and Filter */}
          <div className="stock-adj-controls-row">
            <div className="stock-adj-search-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-search stock-adj-search-icon"
                aria-hidden="true"
              >
                <path d="m21 21-4.34-4.34"></path>
                <circle cx="11" cy="11" r="8"></circle>
              </svg>
              <input
                className="stock-adj-search-input"
                placeholder="Search by item, SKU, or user..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <button className="stock-adj-btn-secondary" type="button" onClick={handleFilterOpen}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-funnel w-4 h-4"
                aria-hidden="true"
              >
                <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path>
              </svg>
              Filter
            </button>
          </div>

          {/* Active Filters Display */}
          {selectedFilterLabel !== "Last 2 Month" && (
            <div className="stock-adj-active-filters">
              <span className="stock-adj-active-filters-label">Active Filters:</span>
              <Chip
                label={getFilterLabelDisplay(selectedFilterLabel)}
                onDelete={handleClearFilter}
                size="small"
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontSize: "12px",
                }}
              />
            </div>
          )}

          {/* Table / Loader / Empty State */}
          <div className="stock-adj-table-card">
            {pageState?.loader ? (
              <div className="stock-adj-loader-container">
                <Spinner />
              </div>
            ) : pageState?.dataRows?.length === 0 ? (
              <div className="stock-adj-empty-state">No adjustments found.</div>
            ) : (
              <>
                <div className="stock-adj-table-wrapper">
                  <table className="stock-adj-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left" }}>Date & Time</th>
                        <th style={{ textAlign: "left" }}>Item Details</th>
                        <th style={{ textAlign: "left" }}>Type</th>
                        <th style={{ textAlign: "left" }}>Quantity</th>
                        <th style={{ textAlign: "left" }}>Reason</th>
                        <th style={{ textAlign: "left" }}>Adjusted By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageState?.dataRows?.map((row) => {
                        const qty = parseFloat(row?.adjustedQuantity);
                        const displayQty = `-${qty}`;
                        const qtyClass = "qty-negative";

                        return (
                          <tr
                            key={row.id}
                            className="stock-adj-row"
                            onClick={() => handleRowClick(row)}
                          >
                            <td className="stock-adj-cell-date" style={{ textAlign: "left" }}>
                              {formatDateTime(row?.lastUpdated)}
                            </td>
                            <td className="stock-adj-cell-product" style={{ textAlign: "left" }}>
                              <div className="stock-adj-cell-details">
                                <div className="stock-adj-item-name">{row?.productName}</div>
                                <div className="stock-adj-item-sku">{row?.batch || "N/A"}</div>
                              </div>
                            </td>
                            <td style={{ textAlign: "left" }}>
                              {renderTypeBadge(row?.adjustmentType)}
                            </td>
                            <td className={`stock-adj-cell-qty ${qtyClass}`} style={{ textAlign: "left" }}>
                              {displayQty}
                            </td>
                            <td className="stock-adj-cell-reason" style={{ textAlign: "left" }}>
                              {row?.reason || "N/A"}
                            </td>
                            <td className="stock-adj-cell-auth" style={{ textAlign: "left" }}>
                              {row?.adjustedBy || "NA"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="stock-adj-pagination">
                  <span className="stock-adj-pagination-info">
                    {(pageState?.page - 1) * pageState?.pageSize + 1}–
                    {Math.min(pageState?.page * pageState?.pageSize, pageState?.total)} of{" "}
                    {pageState?.total}
                  </span>
                  <div className="stock-adj-pagination-btns">
                    <button
                      className="stock-adj-pagination-btn"
                      onClick={() => setPageState((old) => ({ ...old, page: old.page - 1 }))}
                      disabled={pageState?.page === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon width={16} height={16} />
                    </button>
                    <button
                      className="stock-adj-pagination-btn"
                      onClick={() => setPageState((old) => ({ ...old, page: old?.page + 1 }))}
                      disabled={pageState?.page * pageState?.pageSize >= pageState?.total}
                      aria-label="Next page"
                    >
                      <ChevronRightIcon width={16} height={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modals */}
        <AdjustStockCreationModal
          open={createStockOpen}
          handleClose={handleClose}
          reset={reset}
          setReset={setReset}
        />
        <AdjustmentDetailsModal
          open={detailsOpen}
          handleClose={handleDetailsClose}
          adjustmentId={selectedAdjustmentId}
        />
        <FilterModal
          open={filterOpen}
          handleClose={handleFilterClose}
          onApplyFilter={handleApplyFilter}
        />
      </div>
    </Navbar>
  );
};

export default StockAdjustment;
